import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactECharts from "echarts-for-react";
import {
    Card,
    Row,
    Col,
    Statistic,
    Table,
    Button,
    Space,
    Spin,
    Typography,
    App,
    Tag,
    Collapse,
    Empty,
    Progress,
    Select,
    Tabs,
    Dropdown,
} from "antd";
import {
    ArrowLeftOutlined,
    DownloadOutlined,
    RiseOutlined,
    FallOutlined,
    SwapOutlined,
    SafetyCertificateOutlined,
    LineChartOutlined,
    BarChartOutlined,
    PercentageOutlined,
} from "@ant-design/icons";
import {
    Wallet,
    TrendingUp,
    BarChart3,
    Activity,
    FileText,
    ArrowUpRight,
    ArrowDownRight,
    Shield,
    TrendingUpDown,
    Layers,
    Boxes,
    User,
} from "lucide-react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import apiService from "../../services/api";
import useTheme from "../../hooks/useTheme";
import { formatChartTooltipValue } from "../../utils/formatters";
import "./ReportDetail.css";

const { Title: AntTitle, Text } = Typography;
const { Option } = Select;

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler,
);

// 提取 Venue 报告子组件，便于循环渲染
const VenueReportSection = ({ venueSummary, reportData, getBaseTrendOption, getVolumeDualAxisOption, formatVolume, translateTradeType, COLORS }) => {
    const { exchange: ex, trade_type: tp } = venueSummary;
    
    const drillDown = useMemo(() => {
        if (!reportData) return null;

        const trends = (reportData.exchange_daily_trends || [])
            .filter(t => t.exchange === ex && t.trade_type === tp)
            .sort((a, b) => a.date.localeCompare(b.date));

        const dates = trends.map(t => t.date);
        const volumes = trends.map(t => t.volume);
        const makerVolumes = trends.map(t => t.maker_volume || 0);
        const pnls = trends.map(t => t.pnl);
        const rots = trends.map(t => t.rot);

        // Account breakdowns
        const accTrendsMap = new Map();
        (reportData.venue_account_trends || [])
            .filter(t => t.exchange === ex && t.trade_type === tp)
            .forEach(t => {
                if (!accTrendsMap.has(t.account)) accTrendsMap.set(t.account, []);
                accTrendsMap.get(t.account).push(t);
            });

        const accBreakdowns = Array.from(accTrendsMap.entries()).map(([acc, data]) => ({
            account: acc,
            dates: data.sort((a,b) => a.date.localeCompare(b.date)).map(d => d.date),
            volumes: data.sort((a,b) => a.date.localeCompare(b.date)).map(d => d.volume),
            makerVolumes: data.sort((a,b) => a.date.localeCompare(b.date)).map(d => d.maker_volume || 0),
            totalVolume: data.reduce((s,d) => s+d.volume, 0)
        })).sort((a,b) => b.totalVolume - a.totalVolume);

        // Pair breakdowns
        const pairTrendsMap = new Map();
        (reportData.venue_pair_trends || [])
            .filter(t => t.exchange === ex && t.trade_type === tp)
            .forEach(t => {
                if (!pairTrendsMap.has(t.pair)) pairTrendsMap.set(t.pair, []);
                pairTrendsMap.get(t.pair).push(t);
            });

        const pairBreakdowns = Array.from(pairTrendsMap.entries()).map(([pair, data]) => ({
            pair: pair,
            dates: data.sort((a,b) => a.date.localeCompare(b.date)).map(d => d.date),
            volumes: data.sort((a,b) => a.date.localeCompare(b.date)).map(d => d.volume),
            makerVolumes: data.sort((a,b) => a.date.localeCompare(b.date)).map(d => d.maker_volume || 0),
            totalVolume: data.reduce((s,d) => s+d.volume, 0)
        })).sort((a,b) => b.totalVolume - a.totalVolume);

        return { dates, volumes, makerVolumes, pnls, rots, accBreakdowns, pairBreakdowns };
    }, [reportData, ex, tp]);

    if (!drillDown) return null;

    const items = [
        {
            key: 'sub-acc',
            label: (
                <span>
                    <User size={14} style={{ marginRight: 6 }} />
                    <strong>分账号贡献趋势 ({drillDown.accBreakdowns.length})</strong>
                </span>
            ),
            children: (
                <div className="sub-grid">
                    {drillDown.accBreakdowns.map((acc, i) => (
                        <Card key={acc.account} title={acc.account} size="small" className="sub-chart-card" extra={<span className="venue-tag-mini">{formatVolume(acc.totalVolume)}</span>}>
                            <ReactECharts option={getBaseTrendOption(acc.dates, acc.volumes, acc.account, COLORS.series[i % 5].primary)} style={{ height: "160px" }} />
                        </Card>
                    ))}
                </div>
            )
        },
        {
            key: 'sub-pair',
            label: (
                <span>
                    <Boxes size={14} style={{ marginRight: 6 }} />
                    <strong>交易对热度分布 ({drillDown.pairBreakdowns.length})</strong>
                </span>
            ),
            children: (
                <div className="sub-grid">
                    {drillDown.pairBreakdowns.map((p, i) => (
                        <Card key={p.pair} title={p.pair} size="small" className="sub-chart-card" extra={<span className="venue-tag-mini">{formatVolume(p.totalVolume)}</span>}>
                            <ReactECharts option={getBaseTrendOption(p.dates, p.volumes, p.pair, COLORS.series[i % 5].primary)} style={{ height: "160px" }} />
                        </Card>
                    ))}
                </div>
            )
        }
    ];

    return (
        <Card className="venue-analysis-card" title={
            <Space>
                <Layers size={16} />
                <span style={{ fontSize: 16, fontWeight: 600 }}>{ex} ({translateTradeType(tp)}) 深度分析报告</span>
                <Tag color="blue" bordered={false}>{formatVolume(venueSummary.total_volume)} Turnover</Tag>
            </Space>
        } style={{ marginBottom: 32 }}>
            <div className="venue-content">
                <Row gutter={[16, 16]} className="trends-row">
                    <Col xs={24} lg={8}>
                        <Card size="small" title={<span><BarChartOutlined /> 交易量走势 (左:总量 / 右:Maker)</span>} className="trend-main-card">
                            <ReactECharts option={getVolumeDualAxisOption(drillDown.dates, drillDown.volumes, drillDown.makerVolumes)} style={{ height: "240px" }} />
                        </Card>
                    </Col>
                    <Col xs={24} lg={8}>
                        <Card size="small" title={<span><TrendingUpDown size={16} /> 盈亏走势</span>} className="trend-main-card">
                            <ReactECharts option={getBaseTrendOption(drillDown.dates, drillDown.pnls, 'PnL', '#52c41a')} style={{ height: "240px" }} />
                        </Card>
                    </Col>
                    <Col xs={24} lg={8}>
                        <Card size="small" title={<span><PercentageOutlined /> 效率走势 (ROT bps)</span>} className="trend-main-card">
                            <ReactECharts option={getBaseTrendOption(drillDown.dates, drillDown.rots, 'ROT', '#faad14')} style={{ height: "240px" }} />
                        </Card>
                    </Col>
                </Row>

                <Collapse ghost className="sub-collapse" items={items} />
            </div>
        </Card>
    );
};

const ReportDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isDark } = useTheme();
    const { message: msg } = App.useApp();
    const [loading, setLoading] = useState(true);
    const [report, setReport] = useState(null);
    const [reportData, setReportData] = useState(null);

    const COLORS = {
        primary: "#1890ff",
        success: "#52c41a",
        warning: "#faad14",
        error: "#ff4d4f",
        purple: "#722ed1",
        series: [
            { primary: "#1890ff", secondary: "rgba(24, 144, 255, 0.15)" },
            { primary: "#52c41a", secondary: "rgba(82, 196, 26, 0.15)" },
            { primary: "#faad14", secondary: "rgba(250, 173, 20, 0.15)" },
            { primary: "#f5222d", secondary: "rgba(245, 34, 45, 0.15)" },
            { primary: "#722ed1", secondary: "rgba(114, 46, 209, 0.15)" },
        ],
    };

    const formatVolume = useCallback((val) => {
        if (!val && val !== 0) return "0";
        const absVal = Math.abs(val);
        let formatted = val.toFixed(2);
        if (absVal >= 1000000) formatted = (val / 1000000).toFixed(1) + "M";
        else if (absVal >= 1000) formatted = (val / 1000).toFixed(1) + "K";
        return formatted;
    }, []);

    const translateTradeType = useCallback((type) => {
        if (!type) return "";
        const lower = type.toLowerCase();
        if (lower === "spot") return "现货";
        if (lower === "swap" || lower === "futures") return "合约";
        return type;
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [reportRes, dataRes] = await Promise.all([
                apiService.reports.getReport(id),
                apiService.reports.getReportData(id),
            ]);

            if (reportRes.data.success) {
                setReport(reportRes.data.data);
            }
            if (dataRes.data.success) {
                setReportData(dataRes.data.data);
            }
        } catch (error) {
            console.error("加载报告详情失败:", error);
            msg.error("加载报告详情失败");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllData();
    }, [id]);

    const getEquityOption = useMemo(() => {
        if (!reportData?.daily_equity_trends) return {};
        const dates = reportData.daily_equity_trends.map(t => t.date).sort();
        const netAssets = reportData.daily_equity_trends.map(t => t.total_net_assets);
        const pnls = reportData.daily_equity_trends.map(t => t.total_pnl);
        const textColor = isDark ? "rgba(255, 255, 255, 0.45)" : "#64748b";

        return {
            backgroundColor: "transparent",
            tooltip: {
                trigger: "axis",
                axisPointer: { type: "cross" },
                formatter: (params) => {
                    if (!params?.length) return "";
                    let html = `<div style="margin-bottom:4px;font-weight:600">${params[0].axisValue}</div>`;
                    params.forEach((p) => {
                        html += `<div style="display:flex;justify-content:space-between;gap:20px;"><span>${p.marker} ${p.seriesName}</span><b>${formatChartTooltipValue(p.value)}</b></div>`;
                    });
                    return html;
                },
            },
            legend: { bottom: 0, data: ["净资产", "当日盈亏"], textStyle: { color: textColor } },
            grid: { left: "3%", right: "4%", bottom: "15%", top: "10%", containLabel: true },
            xAxis: { type: "category", data: dates, axisLabel: { color: textColor } },
            yAxis: [
                { type: "value", name: "净资产", nameTextStyle: { color: textColor }, scale: true, axisLabel: { color: textColor } },
                { type: "value", name: "盈亏", nameTextStyle: { color: textColor }, scale: true, axisLabel: { color: textColor }, splitLine: { show: false } }
            ],
            series: [
                {
                    name: "净资产",
                    type: "line",
                    data: netAssets,
                    smooth: true,
                    lineStyle: { width: 3, color: "#1890ff" },
                    itemStyle: { color: "#1890ff" },
                    areaStyle: { color: 'rgba(24,144,255,0.1)' },
                    label: {
                        show: true,
                        position: 'top',
                        color: textColor,
                        fontSize: 10,
                        formatter: (params) => formatVolume(params.value)
                    }
                },
                {
                    name: "当日盈亏",
                    type: "bar",
                    yAxisIndex: 1,
                    data: pnls,
                    itemStyle: { color: (params) => params.value >= 0 ? "#52c41a" : "#f5222d" },
                    label: {
                        show: true,
                        position: 'top',
                        color: textColor,
                        fontSize: 10,
                        formatter: (params) => formatVolume(params.value)
                    }
                }
            ]
        };
    }, [reportData, isDark, formatVolume, formatChartTooltipValue]);

    const getBaseTrendOption = useCallback((dates, data, name, color, type = 'line') => {
        const textColor = isDark ? "rgba(255, 255, 255, 0.45)" : "#64748b";
        return {
            backgroundColor: "transparent",
            tooltip: {
                trigger: "axis",
                axisPointer: { type: "line" },
                formatter: (params) => {
                    if (!params?.length) return "";
                    let html = `<div style="margin-bottom:4px;">${params[0].axisValue}</div>`;
                    params.forEach((p) => {
                        html += `<div style="display:flex;justify-content:space-between;gap:16px;"><span>${p.marker} ${p.seriesName}</span><b>${formatChartTooltipValue(p.value)}</b></div>`;
                    });
                    return html;
                },
            },
            grid: { left: "3%", right: "4%", bottom: "10%", top: "15%", containLabel: true },
            xAxis: { type: "category", data: dates, axisLabel: { color: textColor } },
            yAxis: { type: "value", axisLabel: { color: textColor }, splitLine: { lineStyle: { type: 'dashed', color: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' } } },
            series: [{
                name: name,
                type: type,
                data: data,
                smooth: true,
                symbol: 'circle',
                symbolSize: 6,
                lineStyle: { width: 3 },
                itemStyle: { color: color },
                label: {
                    show: true,
                    position: 'top',
                    color: textColor,
                    fontSize: 10,
                    formatter: (params) => {
                        if (name === 'ROT') return params.value.toFixed(2);
                        return formatVolume(params.value);
                    }
                },
                areaStyle: type === 'line' ? { color: {
                    type: 'linear',
                    x: 0, y: 0, x2: 0, y2: 1,
                    colorStops: [
                        { offset: 0, color: color + '33' },
                        { offset: 1, color: color + '00' }
                    ]
                }} : undefined
            }]
        };
    }, [isDark, formatChartTooltipValue, formatVolume]);

    // 双Y轴交易量图表配置 - 左侧总交易量，右侧Maker成交量
    const getVolumeDualAxisOption = useCallback((dates, volumes, makerVolumes) => {
        const textColor = isDark ? "rgba(255, 255, 255, 0.45)" : "#64748b";
        return {
            backgroundColor: "transparent",
            tooltip: {
                trigger: "axis",
                axisPointer: { type: "cross" },
                formatter: (params) => {
                    let result = params[0].axisValue + '<br/>';
                    params.forEach(p => {
                        result += `${p.marker} ${p.seriesName}: ${formatChartTooltipValue(p.value)}<br/>`;
                    });
                    return result;
                }
            },
            legend: {
                bottom: 0,
                data: ["总交易量", "Maker量"],
                textStyle: { color: textColor }
            },
            grid: { left: "3%", right: "4%", bottom: "15%", top: "10%", containLabel: true },
            xAxis: { type: "category", data: dates, axisLabel: { color: textColor } },
            yAxis: [
                {
                    type: "value",
                    name: "总交易量",
                    nameTextStyle: { color: "#1890ff" },
                    axisLabel: { color: "#1890ff", formatter: (val) => formatVolume(val) },
                    splitLine: { lineStyle: { type: 'dashed', color: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' } }
                },
                {
                    type: "value",
                    name: "Maker量",
                    nameTextStyle: { color: "#52c41a" },
                    axisLabel: { color: "#52c41a", formatter: (val) => formatVolume(val) },
                    splitLine: { show: false }
                }
            ],
            series: [
                {
                    name: "总交易量",
                    type: "bar",
                    data: volumes,
                    itemStyle: { color: "#1890ff" },
                    label: {
                        show: true,
                        position: 'top',
                        color: textColor,
                        fontSize: 9,
                        formatter: (params) => formatVolume(params.value)
                    }
                },
                {
                    name: "Maker量",
                    type: "line",
                    yAxisIndex: 1,
                    data: makerVolumes,
                    smooth: true,
                    symbol: 'circle',
                    symbolSize: 6,
                    lineStyle: { width: 3, color: "#52c41a" },
                    itemStyle: { color: "#52c41a" },
                    label: {
                        show: true,
                        position: 'top',
                        color: textColor,
                        fontSize: 9,
                        formatter: (params) => formatVolume(params.value)
                    }
                }
            ]
        };
    }, [isDark, formatVolume, formatChartTooltipValue]);

    const exchangeColumns = [
        { title: "交易所", dataIndex: "exchange", key: "exchange" },
        { 
            title: "类型", 
            dataIndex: "trade_type", 
            key: "trade_type",
            render: (tp) => (
                <Tag color={tp === 'futures' ? 'blue' : 'orange'}>
                    {translateTradeType(tp)}
                </Tag>
            )
        },
        {
            title: "总交易量",
            dataIndex: "total_volume",
            key: "total_volume",
            render: (val) => val.toLocaleString(undefined, { minimumFractionDigits: 2 }),
            sorter: (a, b) => a.total_volume - b.total_volume
        },
        {
            title: "Maker成交量",
            dataIndex: "total_maker_volume",
            key: "total_maker_volume",
            render: (val) => val ? val.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '0.00',
            sorter: (a, b) => a.total_maker_volume - b.total_maker_volume
        },
        {
            title: "Taker成交量",
            dataIndex: "total_taker_volume",
            key: "total_taker_volume",
            render: (val) => val ? val.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '0.00',
            sorter: (a, b) => a.total_taker_volume - b.total_taker_volume
        },
        {
            title: "总PnL",
            dataIndex: "total_pnl",
            key: "total_pnl",
            render: (val) => (
                <span style={{ color: val >= 0 ? "#52c41a" : "#f5222d" }}>
                    {val.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
            ),
            sorter: (a, b) => a.total_pnl - b.total_pnl
        },
        {
            title: "ROT (bps)",
            dataIndex: "rot",
            key: "rot",
            render: (val) => (
                <span style={{ fontWeight: '500' }}>
                    {val?.toFixed(2)}
                </span>
            ),
            sorter: (a, b) => (a.rot || 0) - (b.rot || 0)
        }
    ];

    const [downloading, setDownloading] = useState(false);

    const handleDownload = async (format = 'excel') => {
        setDownloading(true);
        try {
            const response = await apiService.reports.downloadReport(id, format);
            
            // Handle blob response
            const blob = new Blob([response.data], { 
                type: response.headers['content-type'] 
            });
            
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Extracts filename from content-disposition if possible
            const contentDisposition = response.headers['content-disposition'];
            let filename = `report_${id}.${format === 'excel' ? 'xlsx' : format === 'word' ? 'docx' : 'pdf'}`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match) filename = match[1];
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            link.remove();
            window.URL.revokeObjectURL(url);
            msg.success(`报告 (${format.toUpperCase()}) 下载成功`);
        } catch (err) {
            console.error("Download error:", err);
            msg.error("下载报告失败: " + (err.apiMessage || err.message));
        } finally {
            setDownloading(false);
        }
    };

    const downloadItems = [
        {
            key: 'excel',
            label: 'Excel 报告 (数据+图表)',
            icon: <FileText size={16} />,
            onClick: () => handleDownload('excel')
        },
        {
            key: 'word',
            label: 'Word 报告 (精美图文)',
            icon: <FileText size={16} />,
            onClick: () => handleDownload('word')
        },
        {
            key: 'pdf',
            label: 'PDF 报告 (便携预览)',
            icon: <FileText size={16} />,
            onClick: () => handleDownload('pdf')
        }
    ];

    if (loading) return <Spin size="large" tip="加载报告中..." fullscreen spinning />;
    if (!report) return <div className="error-container"><Empty description="未找到报告" /><Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/reports")}>返回列表</Button></div>;

    const sortedExchanges = [...(reportData?.exchange_summaries || [])].sort((a,b) => b.total_volume - a.total_volume);

    return (
        <div className="report-detail-container">
            <div className="detail-header">
                <div className="header-left">
                    <Space size="middle">
                        <Button shape="circle" icon={<ArrowLeftOutlined />} onClick={() => navigate("/reports")} />
                        <div>
                            <AntTitle level={3} style={{ margin: 0 }}>{report.name}</AntTitle>
                            <Text type="secondary">{report.start_date} 至 {report.end_date}</Text>
                        </div>
                    </Space>
                </div>
                <Dropdown menu={{ items: downloadItems }} placement="bottomRight">
                    <Button 
                        type="primary" 
                        icon={<DownloadOutlined />} 
                        loading={downloading} 
                        disabled={report.status !== "completed"} 
                        className="download-btn"
                    >
                        下载报告 <DownloadOutlined />
                    </Button>
                </Dropdown>
            </div>

            <div className="stats-grid-6">
                <Card className="stat-card blue-glass" variant="borderless">
                    <div className="stat-top"><div className="stat-icon-wrapper"><Wallet size={18} color="white" /></div><div className="stat-label">期末净资产</div></div>
                    <div className="stat-value">{(reportData?.total_net_assets || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} <small>USDT</small></div>
                </Card>
                <Card className={`stat-card ${(reportData?.total_pnl || 0) >= 0 ? 'green-glass' : 'red-glass'}`} variant="borderless">
                    <div className="stat-top"><div className="stat-icon-wrapper"><TrendingUp size={18} color="white" /></div><div className="stat-label">阶段盈亏</div></div>
                    <div className="stat-value"><ArrowUpRight size={18} color="white" />{(reportData?.total_pnl || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                </Card>
                <Card className="stat-card purple-glass" variant="borderless">
                    <div className="stat-top"><div className="stat-icon-wrapper"><BarChart3 size={18} color="white" /></div><div className="stat-label">总交易量</div></div>
                    <div className="stat-value">{formatVolume(reportData?.total_volume || 0)} <small>USDT</small></div>
                </Card>
                <Card className="stat-card cyan-glass" variant="borderless">
                    <div className="stat-top"><div className="stat-icon-wrapper"><Activity size={18} color="white" /></div><div className="stat-label">交易所</div></div>
                    <div className="stat-value">{reportData?.exchange_count || 0}</div>
                </Card>
                <Card className="stat-card orange-glass" variant="borderless">
                    <div className="stat-top"><div className="stat-icon-wrapper"><FileText size={18} color="white" /></div><div className="stat-label">活跃账号</div></div>
                    <div className="stat-value">{reportData?.account_count || 0}</div>
                </Card>
                <Card className="stat-card indigo-glass" variant="borderless">
                    <div className="stat-top"><div className="stat-icon-wrapper"><SwapOutlined style={{ color: 'white', fontSize: 16 }} /></div><div className="stat-label">活跃对</div></div>
                    <div className="stat-value">{reportData?.pair_count || 0}</div>
                </Card>
            </div>

            <Collapse ghost defaultActiveKey={['venueTrends', 'equity', 'breakdown']} className="main-collapse" items={[
                {
                    key: 'venueTrends',
                    label: (
                        <span>
                            <Layers size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                            <strong>交易所分项深度报告</strong>
                        </span>
                    ),
                    children: (
                        <>
                            {sortedExchanges.map(ex => (
                                <VenueReportSection
                                    key={`${ex.exchange}|${ex.trade_type}`}
                                    venueSummary={ex}
                                    reportData={reportData}
                                    getBaseTrendOption={getBaseTrendOption}
                                    getVolumeDualAxisOption={getVolumeDualAxisOption}
                                    formatVolume={formatVolume}
                                    translateTradeType={translateTradeType}
                                    COLORS={COLORS}
                                />
                            ))}
                            {sortedExchanges.length === 0 && <Empty description="无交易所数据" />}
                        </>
                    )
                },
                {
                    key: 'equity',
                    label: (
                        <span>
                            <TrendingUp size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                            <strong>全市场资金盈亏总趋势</strong>
                        </span>
                    ),
                    children: (
                        <Card className="main-chart-card"><ReactECharts option={getEquityOption} style={{ height: "400px" }} /></Card>
                    )
                },
                ...(reportData?.hedge_group_summaries?.length > 0 ? [{
                    key: 'hedge',
                    label: (
                        <span>
                            <Shield size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                            <strong>对冲组合分析</strong>
                        </span>
                    ),
                    children: (
                        <Row gutter={[16, 16]}>
                            {reportData.hedge_group_summaries.map(hg => (
                                <Col xs={24} md={12} key={hg.group_id}>
                                    <Card className="hedge-group-card" title={hg.group_name} extra={<Tag color="blue">对冲组</Tag>}>
                                        <div className="hedge-stats">
                                            <div className="hedge-stat-item"><div className="label">组合盈亏</div><div className={`value ${hg.total_pnl >= 0 ? 'up' : 'down'}`}>{hg.total_pnl >= 0 ? '+' : ''}{hg.total_pnl.toLocaleString()}</div></div>
                                            <div className="hedge-stat-item"><div className="label">对冲效率</div><div className="value"><Space direction="vertical" style={{ width: '100%' }}><span>{hg.hedge_efficiency.toFixed(1)}%</span><Progress percent={hg.hedge_efficiency} size="small" showInfo={false} strokeColor={{ '0%': '#10b981', '100%': '#3b82f6' }} /></Space></div></div>
                                        </div>
                                        <div className="hedge-members"><div className="member"><Tag color="orange">Role A</Tag> {hg.member_a}</div><SwapOutlined style={{ color: '#ccc' }} /><div className="member"><Tag color="cyan">Role B</Tag> {hg.member_b}</div></div>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    )
                }] : []),
                {
                    key: 'breakdown',
                    label: (
                        <span>
                            <Activity size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                            <strong>数据详情对照表</strong>
                        </span>
                    ),
                    children: (
                        <Row gutter={[16, 16]}>
                            <Col xs={24} lg={11}><Card title="交易所统计" className="table-card"><Table dataSource={reportData?.exchange_summaries || []} columns={exchangeColumns} rowKey={(record) => `${record.exchange}-${record.trade_type}`} pagination={false} size="small" /></Card></Col>
                            <Col xs={24} lg={13}>
                                <Card title="账号数据细分" className="table-card">
                                    <Table dataSource={reportData?.account_summaries || []} columns={[
                                        { title: "账号", dataIndex: "account", key: "account" },
                                        { title: "交易所", dataIndex: "exchange", key: "exchange" },
                                        { title: "业务线", dataIndex: "business_line_name", key: "business_line_name", ellipsis: true, render: (v) => v || "—" },
                                        { title: "策略类型", dataIndex: "strategy_type_name", key: "strategy_type_name", ellipsis: true, render: (v) => v || "—" },
                                        { title: "范围盈亏", dataIndex: "pnl", key: "pnl", render: (v) => <span style={{ color: v >= 0 ? '#10b981' : '#ef4444', fontWeight: 'bold' }}>{v >= 0 ? '+' : ''}{v?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>, sorter: (a, b) => a.pnl - b.pnl },
                                        { title: "成交量", dataIndex: "total_volume", key: "total_volume", render: (v) => formatVolume(v) },
                                        { title: "Maker量", dataIndex: "maker_volume", key: "maker_volume", render: (v) => formatVolume(v) },
                                        { title: "Taker量", dataIndex: "taker_volume", key: "taker_volume", render: (v) => formatVolume(v) },
                                        { title: "净资产", dataIndex: "net_assets", key: "net_assets", render: (v) => v?.toLocaleString() },
                                    ]} rowKey={(record) => `${record.account}-${record.exchange}-${record.trade_type}`} pagination={{ pageSize: 8 }} size="small" />
                                </Card>
                            </Col>
                        </Row>
                    )
                }
            ]} />
        </div>
    );
};

export default ReportDetail;
